import React, { useState, useRef } from 'react';
import { useToast } from './Toast';
import { getAccessToken } from '../utils/api';
import { Modal, Button, Card, Alert } from './ui';

const CSVImporter = ({ onImport, onClose }) => {
  const toast = useToast();
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [errors, setErrors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const fileInputRef = useRef();

  // CSVサンプルテンプレートのダウンロード
  const downloadTemplate = () => {
    const template = `name,position,project_name,planner,skills,engineer_status,phase,client_company,monthly_rate,project_start_date,project_end_date,project_location
田中太郎,マネージャー,ECサイト開発,温水,"React,Node.js,AWS",アサイン済,"設計,開発,テスト",株式会社サンプル,800000,2026-04-01,2026-09-30,指定なし
山田花子,チーフ,AIシステム構築,瞎事山,"Python,TensorFlow,Docker",未アサイン,"要件定義,設計",,,,
鈴木次郎,なし,モバイルアプリ開発,上前,"React Native,TypeScript",未アサイン,"開発,テスト",,,,`;

    const bom = '\uFEFF';
    const blob = new Blob([bom + template], { type: 'text/csv;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'engineer_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  // CSVファイルの読み込み
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
        setErrors(['CSVファイルを選択してください']);
        return;
      }
      setFile(selectedFile);
      parseCSV(selectedFile);
    }
  };

  // CSV解析（より堅牢な解析）
  const parseCSV = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const csv = e.target.result;
      const lines = csv.split(/\r?\n/).filter(line => line.trim() !== ''); // 空行を除去（\r\n対応）
      
      if (lines.length < 2) {
        setErrors(['CSVファイルにデータが含まれていません']);
        return;
      }

      // ヘッダー行の解析（より堅牢に）
      const headerLine = lines[0];
      const headers = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < headerLine.length; i++) {
        const char = headerLine[i];
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          headers.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      headers.push(current.trim()); // 最後のヘッダー
      
      const requiredHeaders = ['name', 'project_name', 'planner', 'skills', 'engineer_status', 'phase'];
      const missingHeaders = requiredHeaders.filter(header => !headers.includes(header));
      
      if (missingHeaders.length > 0) {
        setErrors([`必須項目が不足しています: ${missingHeaders.join(', ')}`]);
        return;
      }

      const data = [];
      const validationErrors = [];

      // データ行の解析
      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (line === '') continue;
        
        // CSV行の解析（引用符内のカンマを考慮）
        const values = [];
        let current = '';
        let inQuotes = false;
        
        for (let j = 0; j < line.length; j++) {
          const char = line[j];
          if (char === '"') {
            inQuotes = !inQuotes;
          } else if (char === ',' && !inQuotes) {
            values.push(current.trim().replace(/^"(.*)"$/, '$1'));
            current = '';
          } else {
            current += char;
          }
        }
        values.push(current.trim().replace(/^"(.*)"$/, '$1')); // 最後の値

        // 値が不足している場合は空文字で埋める
        while (values.length < headers.length) {
          values.push('');
        }

        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });

        // バリデーション（フォームの必須項目に合わせる）
        if (!row.name) {
          validationErrors.push(`行 ${i + 1}: 名前は必須です`);
        }
        if (!row.project_name) {
          validationErrors.push(`行 ${i + 1}: プロジェクト名は必須です`);
        }
        if (!row.planner) {
          validationErrors.push(`行 ${i + 1}: プランナーは必須です`);
        }
        if (!row.engineer_status) {
          validationErrors.push(`行 ${i + 1}: ステータスは必須です（アサイン済 / 未アサイン）`);
        }

        // スキルをカンマ区切りで分割（CSVパーサーが既に引用符を除去済み）
        if (row.skills) {
          row.skills = row.skills.split(',').map(s => s.trim()).filter(Boolean);
        } else {
          row.skills = [];
        }
        
        if (row.phase) {
          row.phase = row.phase.split(',').map(p => p.trim()).filter(Boolean);
        } else {
          row.phase = [];
        }

        data.push({ ...row, rowNumber: i + 1 });
      }

      setErrors(validationErrors);
      setCsvData(data);
    };

    reader.readAsText(file, 'UTF-8');
  };

  // インポート実行
  const handleImport = async () => {
    if (errors.length > 0) {
      toast.warning('エラーを修正してからインポートしてください');
      return;
    }

    setIsProcessing(true);
    setImportProgress(0);

    try {
      // プログレスバーのシミュレーション
      const progressInterval = setInterval(() => {
        setImportProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      const response = await fetch('/api/engineers/bulk-create/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ engineers: csvData }),
      });

      clearInterval(progressInterval);
      setImportProgress(100);

      if (response.ok) {
        const result = await response.json();
        setTimeout(() => {
          onImport(result);
          onClose();
        }, 500);
      } else {
        const errorData = await response.json();
        const errMsg = errorData.error || errorData.detail || 'インポートに失敗しました';
        const errDetails = errorData.errors ? errorData.errors.slice(0, 5) : [];
        setErrors([errMsg, ...errDetails]);
      }
    } catch (error) {
      setErrors([`ネットワークエラー: ${error.message}`]);
    } finally {
      setIsProcessing(false);
      setImportProgress(0);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="CSVインポート"
      size="xl"
      hideFooter
    >
      <p className="text-sm text-prodia-text-secondary mb-6">
        CSVファイルから複数のエンジニアを一括登録できます
      </p>

      {/* テンプレートダウンロード */}
      <Card className="mb-6" padding>
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <h4 className="font-semibold text-prodia-text-primary mb-1">
              <i className="fas fa-download mr-2 text-prodia-primary" />
              テンプレートをダウンロード
            </h4>
            <p className="text-sm text-prodia-text-secondary mb-2">
              適切なフォーマットでCSVファイルを作成するためのテンプレートです
            </p>
            <div className="text-xs text-prodia-text-secondary bg-prodia-background border border-prodia-border px-3 py-2 rounded-lg">
              <i className="fas fa-lightbulb text-warning mr-2" />
              skills と phase は引用符で囲み、カンマ区切りで入力してください
              <br />
              例: &quot;React,Node.js,AWS&quot; / &quot;設計,開発,テスト&quot;
              <br />
              ※必須: name, project_name, planner, skills, engineer_status, phase
              <br />
              ※engineer_statusは「アサイン済」または「未アサイン」
            </div>
          </div>
          <Button onClick={downloadTemplate} size="sm">
            <i className="fas fa-download" />
            テンプレート
          </Button>
        </div>
      </Card>

      {/* ファイル選択 */}
      <div className="mb-6">
        <label className="block text-xs font-medium text-prodia-text-secondary mb-1.5">
          CSVファイルを選択
        </label>
        <div className="flex items-center gap-4">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            className="hidden"
          />
          <Button variant="secondary" onClick={() => fileInputRef.current?.click()}>
            <i className="fas fa-folder-open" />
            ファイルを選択
          </Button>
          {file && (
            <span className="text-sm text-prodia-text-secondary">
              選択済み: {file.name}
            </span>
          )}
        </div>
      </div>

      {/* エラー表示 */}
      {errors.length > 0 && (
        <Alert variant="danger" title="エラーが発生しました" className="mb-6">
          <ul className="list-disc list-inside space-y-1">
            {errors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      {/* インポート進捗 */}
      {isProcessing && (
        <Card className="mb-6 bg-info-bg border-info-border" padding>
          <div className="flex items-center mb-2">
            <i className="fas fa-spinner fa-spin mr-2 text-info" />
            <span className="text-sm text-info-foreground">インポート中...</span>
          </div>
          <div className="w-full bg-prodia-border rounded-full h-2">
            <div
              className="bg-prodia-primary h-2 rounded-full transition-all duration-300"
              style={{ width: `${importProgress}%` }}
            />
          </div>
          <div className="text-sm text-prodia-text-secondary mt-1">{importProgress}%</div>
        </Card>
      )}

      {/* データプレビュー */}
      {csvData.length > 0 && errors.length === 0 && (
        <div className="mb-6">
          <h4 className="font-semibold text-prodia-text-primary mb-3">
            <i className="fas fa-eye mr-2 text-prodia-text-muted" />
            データプレビュー ({csvData.length}件)
          </h4>
          <div className="overflow-x-auto bg-prodia-background border border-prodia-border rounded-xl p-4 max-h-64">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-prodia-border">
                  <th className="text-left p-2 font-semibold text-prodia-text-primary">名前</th>
                  <th className="text-left p-2 font-semibold text-prodia-text-primary">メール</th>
                  <th className="text-left p-2 font-semibold text-prodia-text-primary">役職</th>
                  <th className="text-left p-2 font-semibold text-prodia-text-primary">プロジェクト</th>
                  <th className="text-left p-2 font-semibold text-prodia-text-primary">プランナー</th>
                  <th className="text-left p-2 font-semibold text-prodia-text-primary">ステータス</th>
                </tr>
              </thead>
              <tbody>
                {csvData.slice(0, 5).map((row, index) => (
                  <tr key={index} className="border-b border-prodia-border">
                    <td className="p-2 text-prodia-text-primary">{row.name}</td>
                    <td className="p-2 text-prodia-text-secondary">{row.email}</td>
                    <td className="p-2 text-prodia-text-secondary">{row.position || '-'}</td>
                    <td className="p-2 text-prodia-text-secondary">{row.project_name}</td>
                    <td className="p-2 text-prodia-text-secondary">{row.planner}</td>
                    <td className="p-2 text-prodia-text-secondary">{row.engineer_status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {csvData.length > 5 && (
              <p className="text-center text-prodia-text-muted mt-2 text-sm">
                ...他 {csvData.length - 5} 件
              </p>
            )}
          </div>
        </div>
      )}

      {/* フッター */}
      <div className="flex justify-between items-center pt-4 border-t border-prodia-border mt-2">
        <div className="text-sm text-prodia-text-secondary">
          <i className="fas fa-info-circle mr-2" />
          同名のエンジニアは自動的にスキップされます
        </div>
        <div className="flex gap-3">
          <Button variant="secondary" onClick={onClose}>
            キャンセル
          </Button>
          <Button
            onClick={handleImport}
            disabled={csvData.length === 0 || errors.length > 0 || isProcessing}
          >
            <i className="fas fa-upload" />
            {isProcessing ? 'インポート中...' : `${csvData.length}件をインポート`}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CSVImporter;
